import React, { useState, useRef, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Languages, X, Settings, ChevronDown, Check, Search } from 'lucide-react';
import { getLanguageById, getSpokenLanguagesFor, getCaptionLanguagesFor } from './languageConfig';
import { getFlagDisplay } from './FlagEmoji';

// Case-insensitive substring filter on label and id.
function filterLanguages(list, query) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return list;
  return list.filter(l =>
    l.label.toLowerCase().includes(q) || l.id.toLowerCase().includes(q)
  );
}

// Language Selector dropdown — searchable, source list switches based on
// `agoraFallbackActive`. When false (default), the user sees the full Google
// Translate language set. When true (Google has failed), only the languages
// Agora STT can transcribe / translate into are shown.
function LanguageSelector({
  spokenLanguage,
  captionLanguage,
  onSpokenChange,
  onCaptionChange,
  agoraFallbackActive,
  restrictSpokenToAgora,
}) {
  const [showSpokenDropdown, setShowSpokenDropdown] = useState(false);
  const [showCaptionDropdown, setShowCaptionDropdown] = useState(false);
  const [spokenQuery, setSpokenQuery] = useState('');
  const [captionQuery, setCaptionQuery] = useState('');
  const spokenRef = useRef(null);
  const captionRef = useRef(null);

  const spoken = getLanguageById(spokenLanguage);
  const caption = getLanguageById(captionLanguage);

  // Spoken picker narrows when either Google has failed (agoraFallbackActive)
  // OR the user picked an unsupported spoken language and we auto-reverted
  // (restrictSpokenToAgora). Caption picker only narrows on Google failure.
  const spokenList = useMemo(
    () => getSpokenLanguagesFor(agoraFallbackActive || restrictSpokenToAgora),
    [agoraFallbackActive, restrictSpokenToAgora]
  );
  const captionList = useMemo(
    () => getCaptionLanguagesFor(agoraFallbackActive),
    [agoraFallbackActive]
  );

  const filteredSpoken = useMemo(() => filterLanguages(spokenList, spokenQuery), [spokenList, spokenQuery]);
  const filteredCaption = useMemo(() => filterLanguages(captionList, captionQuery), [captionList, captionQuery]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (spokenRef.current && !spokenRef.current.contains(e.target)) {
        setShowSpokenDropdown(false);
        setSpokenQuery('');
      }
      if (captionRef.current && !captionRef.current.contains(e.target)) {
        setShowCaptionDropdown(false);
        setCaptionQuery('');
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="space-y-3">
      {agoraFallbackActive && (
        <div className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-2 py-1.5">
          Google Translate is unavailable — showing Agora-supported languages only.
        </div>
      )}
      {!agoraFallbackActive && restrictSpokenToAgora && (
        <div className="text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-md px-2 py-1.5">
          Spoken language reverted to English — your previous choice isn't supported by speech recognition. Pick another supported spoken language below.
        </div>
      )}
      <div className="grid grid-cols-2 gap-3">
        {/* Spoken Language */}
        <div className="relative" ref={spokenRef}>
          <label className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 block font-medium">Spoken Language</label>
          <button
            onClick={() => {
              setShowSpokenDropdown(!showSpokenDropdown);
              setShowCaptionDropdown(false);
            }}
            className="w-full flex items-center justify-between bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 text-white text-xs hover:bg-white/10 hover:border-white/20 transition-all"
          >
            <span className="flex items-center gap-2 min-w-0">
              {getFlagDisplay(spoken?.flag, spoken?.shortCode)}
              <span className="truncate">{spoken?.label}</span>
            </span>
            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${showSpokenDropdown ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {showSpokenDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                className="absolute bottom-full left-0 right-0 mb-1 bg-[#0d1829] border border-white/15 rounded-lg shadow-2xl flex flex-col"
                style={{ zIndex: 99999, maxHeight: '15rem' }}
              >
                <div className="sticky top-0 p-1.5 bg-[#0d1829] border-b border-white/10 flex items-center gap-1.5">
                  <Search className="w-3 h-3 text-gray-500 flex-shrink-0 ml-1" />
                  <input
                    type="text"
                    autoFocus
                    value={spokenQuery}
                    onChange={(e) => setSpokenQuery(e.target.value)}
                    placeholder="Search languages..."
                    className="flex-1 bg-white text-black text-xs placeholder:text-gray-500 rounded px-1.5 py-0.5 focus:outline-none"
                  />
                </div>
                <div className="overflow-y-auto">
                  {filteredSpoken.length === 0 && (
                    <div className="px-2.5 py-2 text-xs text-gray-500">No matches</div>
                  )}
                  {filteredSpoken.map(lang => (
                    <button
                      key={lang.id}
                      onClick={() => {
                        onSpokenChange?.(lang.id);
                        setShowSpokenDropdown(false);
                        setSpokenQuery('');
                      }}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-left transition-colors ${
                        spokenLanguage === lang.id
                          ? 'bg-[#6a1fbf]/20 text-white'
                          : 'text-gray-300 hover:bg-white/[0.06]'
                      }`}
                    >
                      {getFlagDisplay(lang.flag, lang.shortCode)}
                      <span className="truncate flex-1">{lang.label}</span>
                      {spokenLanguage === lang.id && <Check className="w-3 h-3 text-[#00c853] flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Caption Language */}
        <div className="relative" ref={captionRef}>
          <label className="text-[10px] text-gray-400 uppercase tracking-wider mb-1.5 block font-medium">Caption Language</label>
          <button
            onClick={() => {
              setShowCaptionDropdown(!showCaptionDropdown);
              setShowSpokenDropdown(false);
            }}
            className="w-full flex items-center justify-between bg-white/[0.06] border border-white/10 rounded-lg px-2.5 py-2 text-white text-xs hover:bg-white/10 hover:border-white/20 transition-all"
          >
            <span className="flex items-center gap-2 min-w-0">
              {getFlagDisplay(caption?.flag, caption?.shortCode)}
              <span className="truncate">{caption?.label}</span>
            </span>
            <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${showCaptionDropdown ? 'rotate-180' : ''}`} />
          </button>
          <AnimatePresence>
            {showCaptionDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -4, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -4, scale: 0.98 }}
                transition={{ duration: 0.15 }}
                className="absolute bottom-full left-0 right-0 mb-1 bg-[#0d1829] border border-white/15 rounded-lg shadow-2xl flex flex-col"
                style={{ zIndex: 99999, maxHeight: '15rem' }}
              >
                <div className="sticky top-0 p-1.5 bg-[#0d1829] border-b border-white/10 flex items-center gap-1.5">
                  <Search className="w-3 h-3 text-gray-500 flex-shrink-0 ml-1" />
                  <input
                    type="text"
                    autoFocus
                    value={captionQuery}
                    onChange={(e) => setCaptionQuery(e.target.value)}
                    placeholder="Search languages..."
                    className="flex-1 bg-white text-black text-xs placeholder:text-gray-500 rounded px-1.5 py-0.5 focus:outline-none"
                  />
                </div>
                <div className="overflow-y-auto">
                  {filteredCaption.length === 0 && (
                    <div className="px-2.5 py-2 text-xs text-gray-500">No matches</div>
                  )}
                  {filteredCaption.map(lang => (
                    <button
                      key={lang.id}
                      onClick={() => {
                        onCaptionChange?.(lang.id);
                        setShowCaptionDropdown(false);
                        setCaptionQuery('');
                      }}
                      className={`w-full flex items-center gap-2 px-2.5 py-2 text-xs text-left transition-colors ${
                        captionLanguage === lang.id
                          ? 'bg-[#6a1fbf]/20 text-white'
                          : 'text-gray-300 hover:bg-white/[0.06]'
                      }`}
                    >
                      {getFlagDisplay(lang.flag, lang.shortCode)}
                      <span className="truncate flex-1">{lang.label}</span>
                      {captionLanguage === lang.id && <Check className="w-3 h-3 text-[#00c853] flex-shrink-0" />}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default function LiveCaptions({
  isEnabled,
  onClose,
  spokenLanguage,
  captionLanguage,
  onSpokenChange,
  onCaptionChange,
  captions = [],
  // When true, Google Translate has failed for this session and BOTH pickers
  // should restrict their options to the Agora-supported subset.
  agoraFallbackActive = false,
  // When true, the user picked a spoken language that Agora STT cannot
  // transcribe — we auto-reverted to English. Narrows the SPOKEN picker
  // (caption picker is unaffected because Google still translates).
  restrictSpokenToAgora = false,
}) {
  const [showSettings, setShowSettings] = useState(false);
  const settingsRef = useRef(null);
  const settingsBtnRef = useRef(null);

  const spoken = getLanguageById(spokenLanguage);
  const caption = getLanguageById(captionLanguage);
  const isTranslating = spokenLanguage !== captionLanguage;
  const lastCaption = captions[captions.length - 1];
  const usingGoogleTranslate = isTranslating && lastCaption?.translatedBy === 'google';
  const usingAgoraTranslate = isTranslating && lastCaption?.translatedBy === 'agora';

  // Close settings popup when clicking outside
  useEffect(() => {
    if (!showSettings) return;
    const handleClickOutside = (e) => {
      if (settingsRef.current && settingsRef.current.contains(e.target)) return;
      if (settingsBtnRef.current && settingsBtnRef.current.contains(e.target)) return;
      setShowSettings(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSettings]);

  if (!isEnabled) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 10 }}
      transition={{ duration: 0.2 }}
      className="w-full overflow-visible"
    >
      {/* Settings popup - floats above the bar */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            ref={settingsRef}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-[10rem] left-4 right-4 md:absolute md:bottom-full md:left-1/2 md:-translate-x-1/2 md:right-auto mb-2 md:w-[420px] md:max-w-[90vw]"
            style={{ zIndex: 99999 }}
          >
            <div className="bg-[#0a1628]/95 backdrop-blur-2xl border border-white/10 rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] p-4 overflow-visible">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-semibold text-white">Caption Settings</span>
                <button
                  onClick={() => setShowSettings(false)}
                  className="h-6 w-6 flex items-center justify-center text-gray-500 hover:text-white hover:bg-white/10 rounded-lg transition-all"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
              <LanguageSelector
                spokenLanguage={spokenLanguage}
                captionLanguage={captionLanguage}
                onSpokenChange={onSpokenChange}
                onCaptionChange={onCaptionChange}
                agoraFallbackActive={agoraFallbackActive}
                restrictSpokenToAgora={restrictSpokenToAgora}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main captions bar */}
      <div className="bg-[#0a1628]/90 backdrop-blur-xl border-t border-white/[0.06]">
        {/* Caption text */}
        <div className="px-6 py-2.5 min-h-[44px] flex items-center justify-center">
          {captions.length > 0 ? (
            <div className="text-center max-w-4xl w-full">
              {captions[captions.length - 1]?.speaker && (
                <span className="text-[11px] font-medium text-[#c4a1f0] mr-2">
                  {captions[captions.length - 1].speaker}:
                </span>
              )}
              <span className="text-white leading-relaxed" style={{ fontSize: '1em' }}>
                {captions[captions.length - 1]?.text}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1">
                <span className="w-1 h-1 bg-[#6a1fbf] rounded-full animate-pulse" />
                <span className="w-1 h-1 bg-[#6a1fbf]/70 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }} />
                <span className="w-1 h-1 bg-[#6a1fbf]/40 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }} />
              </div>
              <p className="text-gray-500 text-xs">Listening for speech...</p>
            </div>
          )}
        </div>

        {/* Status strip */}
        <div className="px-4 py-1 bg-white/[0.02] border-t border-white/[0.04] flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-5 h-5 rounded bg-gradient-to-br from-[#6a1fbf] to-[#00c853] flex items-center justify-center flex-shrink-0">
              <Languages className="w-3 h-3 text-white" />
            </div>
            <span className="text-[11px] text-gray-400 font-medium flex items-center gap-1.5">
              {getFlagDisplay(spoken?.flag, spoken?.shortCode)} {spoken?.label}
              {isTranslating && (
                <>
                  <span className="text-[#6a1fbf]">&#8594;</span>
                  {getFlagDisplay(caption?.flag, caption?.shortCode)} {caption?.label}
                </>
              )}
            </span>
            {isTranslating && (
              <span className="px-1.5 py-0.5 bg-[#6a1fbf]/15 border border-[#6a1fbf]/25 text-[#c4a1f0] text-[9px] rounded-full font-medium">
                Translating
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {usingGoogleTranslate && (
              <span className="text-[9px] text-gray-600 italic">via Google Translate</span>
            )}
            {usingAgoraTranslate && (
              <span className="text-[9px] text-amber-400/70 italic">via Agora (fallback)</span>
            )}
            <div className="flex items-center gap-1.5 mr-1">
              <div className="relative">
                <div className="w-1.5 h-1.5 bg-[#00c853] rounded-full" />
                <div className="absolute inset-0 w-1.5 h-1.5 bg-[#00c853] rounded-full animate-ping opacity-40" />
              </div>
              <span className="text-[10px] text-gray-500 font-medium">Live</span>
            </div>

            <button
              ref={settingsBtnRef}
              onClick={() => setShowSettings(!showSettings)}
              className={`h-6 w-6 flex items-center justify-center rounded-lg transition-all ${
                showSettings
                  ? 'bg-[#6a1fbf]/20 text-[#c4a1f0] border border-[#6a1fbf]/30'
                  : 'text-gray-500 hover:text-white hover:bg-white/[0.06]'
              }`}
              title="Language settings"
            >
              <Settings className="w-3 h-3" />
            </button>
            <button
              onClick={onClose}
              className="h-6 w-6 flex items-center justify-center text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
              title="Close captions"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
